/* import __COLOCATED_TEMPLATE__ from './teammate-attribute-assigner.hbs'; */
/* RESPONSIBLE TEAM: team-ml */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { get, post } from 'embercom/lib/ajax';

interface TeammateAttributeOption {
  id: string;
  value: string;
  position: number;
}

interface TeammateAttribute {
  id: string;
  name: string;
  dataType: string;
  options: TeammateAttributeOption[];
}

interface Signature {
  Args: {
    selectedTeammateIds: number[];
  };
}

export default class TeammateAttributeAssigner extends Component<Signature> {
  @service declare appService: { app: { id: string } };
  @service declare intl: { t: (key: string, options?: Record<string, unknown>) => string };
  @service declare notificationsService: {
    notifyConfirmation: (msg: string) => void;
    notifyError: (msg: string) => void;
  };

  @tracked isModalOpen = false;
  @tracked attributes: TeammateAttribute[] = [];
  @tracked selectedAttributeId: string | null = null;
  @tracked selectedOptionIds: string[] = [];
  @tracked isLoading = false;
  @tracked isSaving = false;

  get selectedAttribute(): TeammateAttribute | undefined {
    return this.attributes.find((a) => a.id === this.selectedAttributeId);
  }

  get canApply(): boolean {
    return this.selectedOptionIds.length > 0 && !this.isSaving;
  }

  get isMultiSelect(): boolean {
    return this.selectedAttribute?.dataType === 'multi_list';
  }

  @action
  async openModal() {
    this.isModalOpen = true;
    this.selectedAttributeId = null;
    this.selectedOptionIds = [];

    if (this.attributes.length === 0) {
      this.isLoading = true;
      try {
        let response = await get(`/ember/wfm/teammate_attributes?app_id=${this.appService.app.id}`);
        this.attributes = response as TeammateAttribute[];
      } catch {
        this.notificationsService.notifyError(
          this.intl.t('settings.teammate-attributes.load-error'),
        );
        this.isModalOpen = false;
      }
      this.isLoading = false;
    }
  }

  @action
  closeModal() {
    this.isModalOpen = false;
    this.selectedAttributeId = null;
    this.selectedOptionIds = [];
  }

  @action
  selectAttribute(attributeId: string) {
    this.selectedAttributeId = attributeId;
    this.selectedOptionIds = [];
  }

  @action
  backToAttributeList() {
    this.selectedAttributeId = null;
    this.selectedOptionIds = [];
  }

  @action
  selectSingleOption(optionValue: string) {
    this.selectedOptionIds = [optionValue];
  }

  @action
  toggleOption(optionValue: string) {
    if (this.selectedOptionIds.includes(optionValue)) {
      this.selectedOptionIds = this.selectedOptionIds.filter((v) => v !== optionValue);
    } else {
      this.selectedOptionIds = [...this.selectedOptionIds, optionValue];
    }
  }

  @action
  async applyToSelected() {
    if (!this.canApply) {
      return;
    }
    this.isSaving = true;

    let value: string | string[] =
      this.selectedOptionIds.length === 1 ? this.selectedOptionIds[0] : this.selectedOptionIds;

    try {
      await post(`/ember/wfm/teammate_attribute_assignments?app_id=${this.appService.app.id}`, {
        admin_ids: this.args.selectedTeammateIds,
        teammate_attribute_id: Number(this.selectedAttributeId),
        value,
      });
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.teammate-attributes.update-success', {
          count: this.args.selectedTeammateIds.length,
        }),
      );
      this.closeModal();
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('settings.teammate-attributes.update-error'),
      );
    }

    this.isSaving = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::Teams::TeammateAttributeAssigner': typeof TeammateAttributeAssigner;
    'settings/teams/teammate-attribute-assigner': typeof TeammateAttributeAssigner;
  }
}
